/**
 * The pure half of host reporting: what to say about the CAD sessions found,
 * and in what order to list them.
 *
 * Kept out of the components because the distinction it encodes is the one this
 * feature exists to preserve — **"cannot detect" is not "detected none"** — and
 * a rule that only exists inside JSX is a rule no test can hold to. M1's exit
 * criterion is that "no CAD host" is *true* on a machine without Revit, so the
 * two must never collapse into the same sentence (Corrections #23).
 */
import type { CadInstance } from "../bimgo";

export type Tone = "ok" | "warn" | "bad" | "idle";

/** Just enough of `HostsView` to describe it; the hook's extras are irrelevant. */
export type HostsSummary =
  | { kind: "unsupported" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "loaded"; instances: readonly CadInstance[] };

/**
 * The instance card's one-line host status.
 *
 * Four distinct sentences for four distinct situations. `unsupported` and an
 * empty `loaded` are the pair that matters: one means the app could not look,
 * the other that it looked and Revit is not running.
 *
 * **"no connected instances", not "no CAD host".** The card is read while the
 * user is looking at Revit, and "no CAD host" states something they can see is
 * false — which reads as the app being wrong rather than as something they can
 * act on. What is actually true is narrower: nothing is *connected*. The word
 * "instances" also matches the picker they would open next, so the status names
 * the thing they are about to go looking at.
 */
export function describeHosts(hosts: HostsSummary): string {
  switch (hosts.kind) {
    case "unsupported":
      return "host detection unavailable";
    case "loading":
      return "checking for connected instances…";
    case "error":
      return "could not check for connected instances";
    case "loaded":
      return hosts.instances.length === 0
        ? "no connected instances"
        : `${hosts.instances.length} connected instance${hosts.instances.length === 1 ? "" : "s"}`;
  }
}

/**
 * One instance, named the way a user would recognise it.
 *
 * **The document title leads**, because that is what the user is looking at on
 * screen — the Revit version distinguishes two instances only when both have the
 * same document open, which is rare, while the document distinguishes them
 * always. A session id would be unambiguous and means nothing to anybody; it
 * belongs in About, with the rest of the technical detail.
 *
 * An untitled document is a real state: a Revit that is open with no document,
 * or one whose title the manifest did not carry. The caller supplies the word so
 * it can be localised.
 */
export function instanceLabel(instance: CadInstance, untitled: string): string {
  const { title, product } = instanceParts(instance, untitled);
  return `${title} — ${product}`;
}

/**
 * The same name in its two halves — the document, and "Revit 2024" — for the
 * sidebar's instance card, which sets one over the other instead of joining
 * them with a dash. `instanceLabel` is built from this so the two cannot drift.
 */
export function instanceParts(instance: CadInstance, untitled: string): { title: string; product: string } {
  const title = instance.documentTitle?.trim();
  const product =
    instance.host_kind === "autocad"
      ? "AutoCAD"
      : instance.host_kind === "revit"
        ? "Revit"
        : instance.host_kind;
  const version = instance.hostVersion ?? instance.revitVersion ?? "unknown";
  return { title: title || untitled, product: `${product} ${version}` };
}

export function hostsTone(hosts: HostsSummary): Tone {
  if (hosts.kind === "error") return "bad";
  if (hosts.kind === "loaded" && hosts.instances.length > 0) return "ok";
  return "idle";
}

/**
 * Oldest session first, and **stably**.
 *
 * Discovery returns manifests in directory order, which is neither meaningful
 * nor guaranteed to survive a prune — a list that reshuffles every fifteen
 * seconds is unreadable while someone is trying to match a card to the Revit
 * window in front of them. `startedAt` is the one field that orders sessions the
 * way the user opened them; `pid` breaks ties (two Revits can start inside the
 * same second) and is unique, so the order is total rather than merely mostly
 * decided.
 */
export function sortInstances(instances: readonly CadInstance[]): CadInstance[] {
  return [...instances].sort((a, b) => {
    const at = Date.parse(a.startedAt);
    const bt = Date.parse(b.startedAt);
    // An unparseable timestamp sorts last rather than poisoning the comparator:
    // NaN comparisons are all false, which leaves the sort order undefined.
    const aValid = !Number.isNaN(at);
    const bValid = !Number.isNaN(bt);
    if (aValid && bValid && at !== bt) return at - bt;
    if (aValid !== bValid) return aValid ? -1 : 1;
    return a.pid - b.pid;
  });
}

// --- vendors (#5) --------------------------------------------------------------

/** The vendor, in the manifest's own vocabulary. Mirrors `lib/skills.ts`. */
export type HostKind = "revit" | "autocad";

/** The order the instance card shows vendors in, and the order `targetSessions` keys them. */
export const HOST_KIND_ORDER: readonly HostKind[] = ["revit", "autocad"];

export function hostKindOf(instance: Pick<CadInstance, "host_kind">): HostKind | null {
  return instance.host_kind === "revit" || instance.host_kind === "autocad" ? instance.host_kind : null;
}

/** Product names, as the picker's per-vendor titles say them. */
export const VENDOR_LABELS: Record<HostKind, string> = { revit: "Revit", autocad: "AutoCAD" };

/**
 * The instances of each vendor, in `HOST_KIND_ORDER`, each list in
 * `sortInstances` order. A vendor with nothing running is absent, not an empty
 * list: the instance card draws one row per entry, and a control for a vendor that
 * is not there would be a dropdown of nothing.
 */
export function groupByVendor(
  instances: readonly CadInstance[],
): Partial<Record<HostKind, CadInstance[]>> {
  const out: Partial<Record<HostKind, CadInstance[]>> = {};
  for (const instance of sortInstances(instances)) {
    const kind = hostKindOf(instance);
    if (!kind) continue;
    (out[kind] ??= []).push(instance);
  }
  return out;
}
